import { useState, useEffect } from "react";
import { Plus, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useFetch } from "../../lib/useData";
import { PageTitle, Btn, Input } from "../../components/admin/ui";

export default function TimingsAdmin() {
  const { data } = useFetch("/admin/timings");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data && !form) setForm(data); }, [data]); // eslint-disable-line

  if (!form) return <p className="text-slate-400">Loading…</p>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAct = (i, k, v) => set("activities", form.activities.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const setWk = (i, v) => set("weekly_schedule", form.weekly_schedule.map((w, idx) => idx === i ? { ...w, activity: v } : w));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/timings", form); toast.success("Timings updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageTitle title="Temple Timings" desc="Update daily hours, aarti and weekly schedule — reflected live on the website">
        <Btn onClick={save} disabled={saving} testid="timings-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}</Btn>
      </PageTitle>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 space-y-4">
          <h3 className="font-semibold">Daily Hours & Aarti</h3>
          <Input label="Morning Hours" value={form.morning_hours} onChange={(v) => set("morning_hours", v)} testid="t-morning" />
          <Input label="Evening Hours" value={form.evening_hours} onChange={(v) => set("evening_hours", v)} testid="t-evening" />
          <Input label="Morning Aarti" value={form.morning_aarti} onChange={(v) => set("morning_aarti", v)} testid="t-morning-aarti" />
          <Input label="Evening Aarti" value={form.evening_aarti} onChange={(v) => set("evening_aarti", v)} testid="t-evening-aarti" />
          <Input label="Darshan Note" value={form.darshan_note} onChange={(v) => set("darshan_note", v)} />
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Other Activities</h3>
            <Btn variant="outline" onClick={() => set("activities", [...(form.activities || []), { name: "", time: "" }])}><Plus className="w-3 h-3" /> Add</Btn>
          </div>
          <div className="space-y-2">
            {(form.activities || []).map((a, i) => (
              <div key={i} className="flex gap-2">
                <input value={a.name} onChange={(e) => setAct(i, "name", e.target.value)} placeholder="Activity" className="flex-1 rounded bg-slate-900 border border-slate-600 px-2 py-1.5 text-sm" />
                <input value={a.time} onChange={(e) => setAct(i, "time", e.target.value)} placeholder="Time" className="w-40 rounded bg-slate-900 border border-slate-600 px-2 py-1.5 text-sm" />
                <button onClick={() => set("activities", form.activities.filter((_, idx) => idx !== i))} className="text-red-400 p-1"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 md:col-span-2">
          <h3 className="font-semibold mb-3">Weekly Schedule</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {(form.weekly_schedule || []).map((w, i) => (
              <div key={i} className="flex gap-2 items-center">
                <span className="w-24 text-sm text-slate-400">{w.day}</span>
                <input value={w.activity} onChange={(e) => setWk(i, e.target.value)} className="flex-1 rounded bg-slate-900 border border-slate-600 px-2 py-1.5 text-sm" data-testid={`wk-${w.day}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
